import React from "react";

import { useExperiment } from "@statsig/react-bindings";

import { Alert, AlertTitle } from "@/components/molecules/Alert";
import TimeIcon from "@/icons/__generated__/Time/Time20";
import { BookListingSecondaryAlert } from "~/components/listings/book-listing/BookListingCard/BookListingSecondaryAlert";
import { ListingData, useListingData } from "~/context/ListingPageDataContext";
import { IFaqs } from "~/types/api-types";

import { useBookListingContext } from "../BookListingContext";
import { BookListingForm } from "../BookListingForm/BookListingForm";
import { FsvPaymentScheduleSection } from "../BookListingForm/FsvPaymentSchedule";

import { BookListingCardFooter } from "./BookListingCardFooter";
import { BookListingCardHeader } from "./BookListingCardHeader/BookListingCardHeader";
import { BookListingCardUnavailable } from "./BookListingCardUnavailable";

interface BookListingCardProps {
  guestNeedsVerification: boolean | undefined;
  faqs?: IFaqs;
  pageTitle: string;
}

export const BookListingCard: React.FC<BookListingCardProps> = ({
  guestNeedsVerification,
  faqs,
  pageTitle,
}) => {
  const { secondaryDataFetched, data: listingData = {} as ListingData } =
    useListingData();
  const { alertConfig, setAlertConfig, showUrgencyMessagingAlert } =
    useBookListingContext();
  const isUrgentListing = useExperiment("listing-urgency-v2").get(
    "variant",
    false,
  );

  const isListingPaused =
    listingData.listingStatus === "PAUSED" ||
    listingData.listingStatus === "PAUSED_INTERNALLY";

  // Show urgency messaging alert on page load
  React.useEffect(() => {
    if (isUrgentListing) {
      showUrgencyMessagingAlert();
    }
  }, [isUrgentListing, showUrgencyMessagingAlert]);

  if (isListingPaused) {
    return <BookListingCardUnavailable />;
  }

  return (
    <>
      <Alert
        className="tw-mb-20 sm:tw-mb-12"
        isVisible={alertConfig.isVisible}
        onDismiss={() => {
          setAlertConfig((prev) => ({
            ...prev,
            isVisible: false,
          }));
        }}
        onUnmount={() => {
          setAlertConfig((prev) => ({
            ...prev,
            message: "",
          }));
        }}
      >
        <TimeIcon />
        <AlertTitle>{alertConfig.message}</AlertTitle>
      </Alert>

      <BookListingSecondaryAlert />

      <div className="tw-border tw-border-gray-200 tw-rounded-4">
        <BookListingCardHeader />

        {/* Desktop renders the schedule inside the fee-calc form, which is
        hidden on mobile web outside the calendar experiment. */}
        <FsvPaymentScheduleSection
          className="tw-p-24 lg:tw-hidden"
          headingId="fsv-payment-schedule-title-mobile"
        />

        {secondaryDataFetched && (
          <div className="tw-p-24 tw-hidden lg:tw-block" id="fee-calc">
            <BookListingForm
              guestNeedsVerification={guestNeedsVerification}
              faqs={faqs}
              pageTitle={pageTitle}
            />

            <BookListingCardFooter />
          </div>
        )}
      </div>
    </>
  );
};
